import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import {
  STATUS_INFO,
  StatusIndicatorIcon,
} from '@/components/staffer/AssignmentsBasedGradesTable/Table/Rows/StatusIndicator';
import { StatusType } from '@/components/staffer/AssignmentsBasedGradesTable/types';
import useGradesTableStore from '../store/useGradesTableStore';
import { StudentRowDataType } from '../utils';
import useQuestionAnswers from '../hooks/useQuestionAnswers';
import CommentButton from './CommentButton';
import TableData from './TableData';
import CommentDrawer from './CommentDrawer';

type TableRowProps = {
  rowData: StudentRowDataType;
};

const TableRow = ({ rowData }: TableRowProps) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const lowScoresSettings = useGradesTableStore(state => state.lowScoresSettings);
  const dueDate = moment(rowData.due_datetime);
  const submittedAt = moment(rowData.submitted_at);
  const score = rowData.total_score_override ?? rowData.total_score;
  const assignmentComments = useQuestionAnswers({ assignmentId: rowData.assignment_id });

  const displayScore = useMemo(() => {
    if (Number.isInteger(score)) {
      return `${score}/${rowData.max_points}`;
    }
    return '';
  }, [score, rowData.max_points]);

  const isLowScore = () => {
    if (!lowScoresSettings.enabled) return false;
    if (!score) return false;

    return score < parseFloat(rowData.max_points) * (lowScoresSettings.threshold / 100.0);
  };

  const status = useMemo<StatusType>(() => {
    if (rowData.submitted_at && isLowScore()) return 'lowScore';
    if (rowData.submitted_at) return 'submitted';
    if (rowData.submission_id) return 'inProgress';

    return 'notStarted';
  }, []);

  return (
    <>
      <tr
        key={rowData.assignment_id}
        className="tw-border-b tw-border-1 tw-border-med-grey tw-border-solid last:tw-border-b-0"
      >
        <TableData>
          {rowData.assignment_details.type}: {rowData.assignment_details.title}
        </TableData>
        <TableData>
          {dueDate.format('MMM D, YYYY')} by {dueDate.format('h:mma')}
        </TableData>
        <TableData>
          {rowData.submitted_at
            ? `${submittedAt.format('MMM D, YYYY')} at ${submittedAt.format('h:mma')}`
            : 'N/A'}
        </TableData>
        <TableData>
          <div className="tw-flex tw-items-center tw-h-full tw-w-full tw-gap-x-2">
            <StatusIndicatorIcon type={status} /> {STATUS_INFO[status].label}
          </div>
        </TableData>
        <TableData>
          {displayScore}
        </TableData>
        <TableData>
          <div className="tw-flex tw-items-center tw-h-full tw-w-full">
            {assignmentComments?.comments?.length > 0 && (
              <CommentButton
                commentDrawer={drawerOpen}
                newCommentCount={assignmentComments.newCommentCount}
                setCommentDrawer={setDrawerOpen}
              />
            )}
          </div>
        </TableData>
      </tr>
      {assignmentComments?.comments
        && (
          <CommentDrawer
            assignmentId={rowData.assignment_id}
            comments={assignmentComments.comments}
            drawerOpen={drawerOpen}
            score={displayScore}
            setDrawerOpen={setDrawerOpen}
          />
        )}
    </>
  );
};

export default TableRow;
